/* eslint-disable arrow-body-style */
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { AddMenuFormValues, CategoryType } from 'src/@types/menu_forms';

import { DB } from 'src/contexts/FirebaseContext';

interface UpdateMenuLogoImageResponse {
  image: string;
  menuId: string;
}

// Define a service using a base URL and expected endpoints
export const MenusApi = createApi({
  reducerPath: 'MenusApi',
  baseQuery: fakeBaseQuery(),
  refetchOnReconnect: true,
  tagTypes: ['Menu', 'Category', 'Product'],
  endpoints: (builder) => ({
    //menus
    addMenu: builder.mutation<Partial<AddMenuFormValues>, Partial<AddMenuFormValues>>({
      async queryFn(args) {
        let menusRef = collection(DB, 'menus');
        let docId = '';
        await addDoc(menusRef, args).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Menu', id: 'LIST' }],
    }),
    addMenuCategory: builder.mutation<Partial<CategoryType>, Partial<CategoryType>>({
      async queryFn(args) {
        let categoriesRef = collection(DB, 'menus', args.menuId as string, 'categories');
        let docId = '';
        await addDoc(categoriesRef, args).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Category', id: 'LIST' }],
    }),

    fetchMenuCategories: builder.query<any, string>({
      async queryFn(menuId) {
        let menuRef = collection(DB, 'menus', menuId, 'categories');
        let categories = (await getDocs(menuRef)).docs.map((doc: any) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });
        return { data: categories };
      },
    }),
    addMenuProduct: builder.mutation<Partial<CategoryType>, Partial<CategoryType>>({
      async queryFn(args) {
        let categoriesRef = collection(DB, 'menus', args.menuId as string, 'products');
        let docId = '';
        await addDoc(categoriesRef, args).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Product', id: 'LIST' }],
    }),
    fetchMenus: builder.query<any, string>({
      async queryFn(ownerId) {
        const constraints = [];
        let menuData: AddMenuFormValues[] = [];

        constraints.push(where('ownerid', '==', ownerId));
        let menusRef = collection(DB, 'menus');
        const qMenus = query(menusRef, ...constraints);
        menuData = (await getDocs(qMenus)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        return { data: menuData };
      },
      providesTags: (result, error, orgId) => [{ type: 'Menu', id: orgId }],
    }),
    fetchMenuProducts: builder.query<any, string>({
      async queryFn(menuId) {
        let menuRef = collection(DB, 'menus', menuId, 'products');
        let categories = (await getDocs(menuRef)).docs.map((doc: any) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });
        return { data: categories };
      },
    }),
    fetchMenu: builder.query<AddMenuFormValues, string>({
      async queryFn(menuId) {
        let menuDocRef = doc(DB, 'menus', menuId);
        let MenuData = (await getDoc(menuDocRef)).data();
        return { data: MenuData as AddMenuFormValues };
      },
      providesTags: (result, error, menuId) => [{ type: 'Menu', id: menuId }],
    }),
    editMenuLogoImage: builder.mutation<string, UpdateMenuLogoImageResponse>({
      async queryFn(args) {
        const { menuId, image } = args;
        let menuDocRef = doc(DB, 'menus', menuId);
        await updateDoc(menuDocRef, {
          logoImage: image,
        });
        return { data: image };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Menu', id: arg.menuId }],
    }),
    editMenu: builder.mutation<boolean, any>({
      async queryFn(args) {
        const { menuId, menuData } = args;
        let menuDocRef = doc(DB, 'menus', menuId);
        updateDoc(menuDocRef, { ...menuData });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Menu', id: arg.menuId }],
    }),
    editMenuProduct: builder.mutation<boolean, any>({
      async queryFn(args) {
        const { menuid, id, product } = args;

        let productDocRef = doc(DB, 'menus', menuid, 'products', id);
        updateDoc(productDocRef, { ...product });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Product', id: arg.id }],
    }),
    removeMenuProduct: builder.mutation<boolean, any>({
      async queryFn({ menuId, productId }) {
        let productDocRef = doc(DB, 'menus', menuId, 'products', productId);
        deleteDoc(productDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Product', id: 'LIST' }],
    }),
    removeMenu: builder.mutation<boolean, string>({
      async queryFn(menuId) {
        let menuDocRef = doc(DB, 'menus', menuId);
        deleteDoc(menuDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Menu', id: 'LIST' }],
    }),
    fetchMenusByOrgId: builder.query<any, string>({
      async queryFn(orgId) {
        const constraints = [];
        let menuData: AddMenuFormValues[] = [];

        constraints.push(where('orgId', '==', orgId));
        let menusRef = collection(DB, 'menus');
        const qMenus = query(menusRef, ...constraints);
        menuData = (await getDocs(qMenus)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        return { data: menuData };
      },
      providesTags: (result, error, orgId) => [{ type: 'Menu', id: orgId }],
    }),
    updateDefaultMenu: builder.mutation({
      queryFn: async ({ menuId, isDefault }) => {
        try {
          const menuRef = doc(DB, 'menus', menuId);
          await updateDoc(menuRef, { isDefault });
          return { data: { id: menuId, isDefault } };
        } catch (error) {
          return { error: 'Failed to update default menu' };
        }
      },
      invalidatesTags: ['Menu'],
    }),
  }),
});
// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useAddMenuMutation,
  useAddMenuCategoryMutation,
  useFetchMenuCategoriesQuery,
  useAddMenuProductMutation,
  useFetchMenusQuery,
  useFetchMenuProductsQuery,
  useFetchMenuQuery,
  useEditMenuLogoImageMutation,
  useEditMenuMutation,
  useEditMenuProductMutation,
  useRemoveMenuProductMutation,
  useRemoveMenuMutation,
  useUpdateDefaultMenuMutation,
  useFetchMenusByOrgIdQuery,
} = MenusApi;
